import {WmWoundDocumentationEntry} from '../../interfaces/wound-management/wm-wound-documentation-entry';
import {LightUser} from '../../interfaces/core/light-user';
import {LightUserFactory} from '../core/light-user-factory';
import {WmWoundAnswer} from '../../interfaces/wound-management/wm-wound-answer';
import {WmWoundAnswerFactory} from './wm-wound-answer-factory';
import {WmWoundImage} from '../../interfaces/wound-management/wm-wound-image';
import {WmWoundImageFactory} from './wm-wound-image-factory';
import {LightEmployee} from '../../interfaces/employees/light-employee';
import {LightEmployeeFactory} from '../employees/light-employee-factory';
import {DateTime} from "luxon";
import {UserService} from "../../services/core/user.service";
import {WmWoundDocumentation} from '../../interfaces/wound-management/wm-wound-documentation';

export class WmWoundDocumentationEntryFactory {

  /**
   *
   * @param wmWoundDocumentationEntry
   */
  public static fromObject(wmWoundDocumentationEntry: any): WmWoundDocumentationEntry {
    return new class implements WmWoundDocumentationEntry {
      bandageChange: string = wmWoundDocumentationEntry.bandage_change;
      user: LightUser = wmWoundDocumentationEntry.user ? LightUserFactory.fromObject(wmWoundDocumentationEntry.user) : null;
      depth: number = wmWoundDocumentationEntry.depth;
      dryPhase: number = wmWoundDocumentationEntry.dry_phase;
      finished: string = wmWoundDocumentationEntry.finished;
      finishedEmployeeId: number = wmWoundDocumentationEntry.finished_employee_id;
      finishedBy: LightEmployee = wmWoundDocumentationEntry.finished_by ?
        LightEmployeeFactory.fromObject(wmWoundDocumentationEntry.finished_by) : null;
      primaryNurseEmployeeId: number = wmWoundDocumentationEntry.primary_nurse_employee_id;
      primaryNurse: LightEmployee =
        LightEmployeeFactory.fromObject(wmWoundDocumentationEntry.primary_nurse);
      intolerances: string = wmWoundDocumentationEntry.intolerances;
      length: number = wmWoundDocumentationEntry.length;
      pocket: number = wmWoundDocumentationEntry.pocket;
      userId: number = wmWoundDocumentationEntry.user_id;
      wetPhase: number = wmWoundDocumentationEntry.wet_phase;
      appointment: string = wmWoundDocumentationEntry.appointment;
      width: number = wmWoundDocumentationEntry.width;
      wmWoundDocumentationId: number = wmWoundDocumentationEntry.wm_wound_documentation_id;
      wmWoundDocumentationUuid: string = wmWoundDocumentationEntry.wm_wound_documentation_uuid;
      createdDate: string = wmWoundDocumentationEntry.created_date;
      isFinal: boolean = wmWoundDocumentationEntry.is_final;
      comment: string = wmWoundDocumentationEntry.comment;
      wmWoundAnswers: WmWoundAnswer[] =
        wmWoundDocumentationEntry.wm_wound_answers ?
          WmWoundAnswerFactory.fromArray(wmWoundDocumentationEntry.wm_wound_answers) : [];
      wmWoundImages: WmWoundImage[] =
        wmWoundDocumentationEntry.wm_wound_images ? WmWoundImageFactory.fromArray(wmWoundDocumentationEntry.wm_wound_images) : [];

      // ------------------------------------------------------------------------------------------------
      id: number = wmWoundDocumentationEntry.id;
      uuid: string = wmWoundDocumentationEntry.uuid;
      modified: string = wmWoundDocumentationEntry.modified;
      created: string = wmWoundDocumentationEntry.created;
      deleted: 0 | 1 = 0;
    };
  }

  /**
   *
   * @param wmWoundDocumentationEntries
   */
  public static fromArray(wmWoundDocumentationEntries: any[]): WmWoundDocumentationEntry[] {
    return wmWoundDocumentationEntries.map(_wmWoundDocumentationEntry => {
      return WmWoundDocumentationEntryFactory.fromObject(_wmWoundDocumentationEntry);
    });
  }

  /**
   *
   * @param wmWoundDocumentationEntry
   */
  public static toRemote(wmWoundDocumentationEntry: WmWoundDocumentationEntry): any {
    return {
      id: wmWoundDocumentationEntry.id,
      uuid: wmWoundDocumentationEntry.uuid,
      bandage_change: wmWoundDocumentationEntry.bandageChange,
      depth: wmWoundDocumentationEntry.depth,
      dry_phase: wmWoundDocumentationEntry.dryPhase,
      intolerances: wmWoundDocumentationEntry.intolerances,
      length: wmWoundDocumentationEntry.length,
      pocket: wmWoundDocumentationEntry.pocket,
      wet_phase: wmWoundDocumentationEntry.wetPhase,
      width: wmWoundDocumentationEntry.width,
      wm_wound_documentation_uuid: wmWoundDocumentationEntry.wmWoundDocumentationUuid,
      created_date: wmWoundDocumentationEntry.createdDate,
      comment: wmWoundDocumentationEntry.comment,
      finished: wmWoundDocumentationEntry.finished,
      finished_employee_id: wmWoundDocumentationEntry.finishedEmployeeId,
      modified: wmWoundDocumentationEntry.modified,
      created: wmWoundDocumentationEntry.created,
      is_final: wmWoundDocumentationEntry.isFinal,
      appointment: wmWoundDocumentationEntry.appointment
    };
  }

  /**
   *
   * @param wmWoundDocumentationEntries
   */
  public static toArray(wmWoundDocumentationEntries: WmWoundDocumentationEntry[]): any[] {
    return wmWoundDocumentationEntries
      .map(_wmWoundDocumentationEntry => WmWoundDocumentationEntryFactory.toRemote(_wmWoundDocumentationEntry));
  }

  /**
   *
   * @param wmWoundDocumentation
   */
  public static new(wmWoundDocumentation: WmWoundDocumentation): WmWoundDocumentationEntry {
    return new class implements WmWoundDocumentationEntry {
      appointment: string = null;
      bandageChange: string = null;
      comment: string = null;
      createdDate: string = DateTime.local().toISODate();
      depth: number = null;
      dryPhase: number = null;
      finished: string = null;
      finishedBy: LightEmployee = null;
      finishedEmployeeId: number = null;
      intolerances: string = null;
      isFinal: boolean = false;
      length: number = null;
      pocket: number = null;
      primaryNurse: LightEmployee = UserService.getUser().employee;
      primaryNurseEmployeeId: number = UserService.getUser().employee.id;
      user: LightUser = UserService.getUser();
      userId: number = UserService.getUser().id;
      wetPhase: number = null;
      width: number = null;
      wmWoundAnswers: WmWoundAnswer[] = [];
      wmWoundDocumentationId: number = wmWoundDocumentation.id;
      wmWoundDocumentationUuid: string = wmWoundDocumentation.uuid;
      wmWoundImages: WmWoundImage[] = [];

      // ------------------------------------------------------------------------------------------------
      id: number = null;
      uuid: string = null;
      modified: string = null;
      created: string = null;
      deleted: 0 | 1 = 0;
    };
  }

}
